import { TRPCClientError, type TRPCLink } from '@trpc/client';
import { observable } from '@trpc/server/observable';

import { AUTHENTICATE_AS } from '@/lib/constants';
import type { AppRouter } from '@/server/api/root';
import { captureException } from '@sentry/react';

async function checkClerkToken(): Promise<void> {
    if (AUTHENTICATE_AS) {
        return;
    }

    const token = await window.Clerk?.session?.getToken();

    if (!token) {
        throw new Error('Clerk token not found');
    }
}

export const authLink: TRPCLink<AppRouter> = () => {
    return ({ next, op }) => {
        return observable((observer) => {
            async function attempt(signal: AbortSignal): Promise<void> {
                try {
                    await checkClerkToken();
                } catch (error) {
                    captureException(error);
                    observer.error(
                        new TRPCClientError(
                            'UNAUTHORIZED',
                            error instanceof Error ? { cause: error } : undefined,
                        ),
                    );
                    window.location.href =
                        '/sign-in?redirect_url=' + encodeURIComponent(window.location.href);

                    return;
                }

                if (signal.aborted) {
                    return;
                }

                const unsubscribe = next(op).subscribe(observer);

                abortController.signal.addEventListener('abort', () => {
                    unsubscribe.unsubscribe();
                });
            }

            const abortController = new AbortController();

            void attempt(abortController.signal);

            return () => {
                abortController.abort();
            };
        });
    };
};
